import { onBeforeUnmount, onMounted, type Ref } from 'vue';
import { Offcanvas } from 'bootstrap';

interface UseOffcanvasOptions {
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * Tears an offcanvas down when its component is going away, whether the panel is open or not.
 *
 * Bootstrap locks the page's scroll (overflow: hidden on <body>) in show() and releases it only in
 * hide()'s completion, which it defers to the end of the transition. So neither obvious teardown
 * works for an open panel: dispose() alone never releases the lock, and hide() followed straight by
 * dispose() is worse — dispose() nulls the instance at once, and the deferred completion then
 * throws before it reaches the lock. Either way the next page cannot scroll.
 *
 * So: hide, and dispose only once Bootstrap reports the panel hidden. `hiding` on the element right
 * after hide() means a completion is pending and that report will come — even after Vue has
 * detached the element, since Bootstrap falls back to a timer. Without it nothing is pending: the
 * panel was not on screen, and it is disposed at once.
 *
 * A `hidden` event at the panel is not always that report, nor always the last thing pending:
 *  - Bootstrap's events bubble, so a drawer nested in the panel reports its own close through the
 *    panel's element. Hence the target check.
 *  - The panel's own report can come from an earlier hide that a reopen interrupted. When a
 *    panel's transition ends, Bootstrap completes every show and hide pending on it at that moment
 *    in one pass, in the order they were called, so the reopen's completion and this hide's are
 *    queued behind that report and would throw on a disposed instance. Hence the timer: it puts
 *    the dispose after the pass. A task, not a microtask — a native transitionend runs microtasks
 *    between its listeners, which is still inside the pass.
 *
 * Call it from onBeforeUnmount, not onUnmounted: by then Vue has nulled the template ref.
 * useOffcanvas does so for its own panel.
 */
export function teardownOffcanvas(el: HTMLElement | null | undefined) {
  if (!el) return;
  const instance = Offcanvas.getInstance(el);
  if (!instance) return;

  instance.hide();
  if (!el.classList.contains('hiding')) {
    instance.dispose();
    return;
  }

  const onHidden = (event: Event) => {
    if (event.target !== el) return;
    el.removeEventListener('hidden.bs.offcanvas', onHidden);
    // Asked twice for the same panel, the second ask finds the instance already disposed.
    setTimeout(() => { if (Offcanvas.getInstance(el) === instance) instance.dispose(); });
  };
  el.addEventListener('hidden.bs.offcanvas', onHidden);
}

export function useOffcanvas(elRef: Ref<HTMLElement | null>, options: UseOffcanvasOptions = {}) {
  const show = () => {
    if (elRef.value) {
      Offcanvas.getOrCreateInstance(elRef.value).show();
      options.onOpen?.();
    }
  };

  const hide = () => {
    if (elRef.value) {
      Offcanvas.getInstance(elRef.value)?.hide();
    }
  };

  // Bootstrap fires `hidden` when a hide's transition ends, but its show() is only blocked while
  // the panel still counts as shown — and hide() clears that at once. So a panel reopened inside
  // the transition is already on its way back in, carrying `showing`, when the earlier hide
  // completes and fires this event. That event is stale: the panel is open, and telling the owner
  // it closed would reset state behind a panel the user is looking at.
  //
  // A real close never arrives with `showing`: completions run in the order they were queued, so
  // any earlier show has finished, and dropped the class, before the last hide reports. The one
  // sequence this does not catch is show → hide → show inside a single transition, where the
  // first show's completion drops `showing` before the stale hide reports; by hand that takes
  // three clicks timed within about 300 ms.
  const onHidden = (event: Event) => {
    const el = elRef.value;
    if (!el || event.target !== el) return;
    if (el.classList.contains('showing')) return;
    options.onClose?.();
  };

  onMounted(() => {
    elRef.value?.addEventListener('hidden.bs.offcanvas', onHidden);
  });

  // The owner can go away with its panel open — a route change, a parent's v-if — and the page it
  // leaves behind must still scroll, so the panel is torn down with it. The owner is not told
  // about the close that causes: it is the one leaving.
  //
  // onBeforeUnmount, not onUnmounted: Vue has nulled the template ref by then, and both of these
  // would silently do nothing.
  onBeforeUnmount(() => {
    elRef.value?.removeEventListener('hidden.bs.offcanvas', onHidden);
    teardownOffcanvas(elRef.value);
  });

  return { show, hide };
}
